<template>
    <SummaryRadioGroup v-if="options.length" v-model="selectedOption" :options="options">
        <template #extraContent="{ selected }">
            <next-step-coaching v-if="selectedOption === FinalSalaryNextSteps.Coaching && hasCoachingOption" />
            <NextStepIFA v-if="selectedOption === FinalSalaryNextSteps.IFA && hasIFAOption" />
        </template>
    </SummaryRadioGroup>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import NextStepCoaching from '@/guiide/components/dboptions/NextStepCoaching.vue';
import NextStepIFA from "@/guiide/components/dboptions/NextStepIFA.vue";
import SummaryRadioGroup from "@/guiide/components/common/SummaryRadioGroup.vue";
import { useUIContent } from "@/guiide/ts/configuration/useUIContent";

    const { htmlArray, bool } = useUIContent();

    type OptionInfo = {
        type: FinalSalaryNextSteps;
        name: string;
        description: Array<string>;
        icon: string;
        abbreviation: string;
    };

    enum FinalSalaryNextSteps {
        Coaching,
        IFA,
        NotReadyToRetire,
        NeedMoreSupport,
        ReadyToRetire
    }

    const props = defineProps({
        schemeSupportMode: { type: String, required: true }
    });

    onMounted(() => {
        selectedOption.value = options.value.length > 0 ? options.value[0].type : FinalSalaryNextSteps.ReadyToRetire;
    });

    const hasCoachingOption = props.schemeSupportMode === "Coaching";
    const hasIFAOption = props.schemeSupportMode == "IFA";

    const selectedOption = ref<FinalSalaryNextSteps>(FinalSalaryNextSteps.ReadyToRetire);

    const options = computed<Array<OptionInfo>>(() => {

        const includeIFA = props.schemeSupportMode == "IFA";
        const includeCoaching = props.schemeSupportMode == "Coaching";
        const includeNotReadyToRetire = bool('dboptions.next-steps.not-ready-to-retire.enabled');
        const includeNeedMoreSupport = bool('dboptions.next-steps.need-more-support.enabled');
        const includeReadyToRetire = bool('dboptions.next-steps.ready-to-retire.enabled');

        const optionsArray = new Array<OptionInfo>();

        if (includeIFA) {
            optionsArray.push({
                type: FinalSalaryNextSteps.IFA,
                name: "I need support with my options",
                description: [""],
                icon: "bi bi-chat-right-text",
                abbreviation: ""
            });
        }

        if (includeCoaching) {
            optionsArray.push({
                type: FinalSalaryNextSteps.Coaching,
                name: "I need support with my options",
                description: [""],
                icon: "bi-mortarboard",
                abbreviation: ""
            });
        }

        if (includeNotReadyToRetire) {
            const description = htmlArray('dboptions.next-steps.not-ready-to-retire.text');
            optionsArray.push({
                type: FinalSalaryNextSteps.NotReadyToRetire,
                name: "I'm not ready to retire yet",
                description,
                icon: "bi-hourglass-split",
                abbreviation: ""
            });
        }

        if (includeNeedMoreSupport) {
            const description = htmlArray('dboptions.next-steps.need-more-support.text');
            optionsArray.push({
                type: FinalSalaryNextSteps.NeedMoreSupport,
                name: "I need more support",
                description,
                icon: "bi-person-raised-hand",
                abbreviation: ""
            });
        }

        if (includeReadyToRetire) {
            const description = htmlArray('dboptions.next-steps.ready-to-retire.text');
            optionsArray.push({
                type: FinalSalaryNextSteps.ReadyToRetire,
                name: "I'm ready to retire",
                description,
                icon: "bi-piggy-bank",
                abbreviation: ""
            });
        }

        return optionsArray;
    });

</script>
